import { IpcRenderer } from 'electron'
import { IRPC } from '@prisma/studio-types'

import deserialize from '../deserialize'
import { ITransportBrowser } from '../types'

export default class ElectronTransport implements ITransportBrowser {
  private requestIdCounter: number = 0
  private ipcRenderer: IpcRenderer
  /**
   * Since electron's IPC is an event emitter and we need the transport to behave like a Promise,
   * we'll save the `resolve` and `reject` methods for each request, and call them later. This is required because
   * we cannot guarantee that a response will come back when we send a request.
   * AKA: We get the response in a different method than the place where we sent the request from. (I'm struggling to explain this succinctly, so reading code below might make this clearer)
   */
  private requests: Map<
    number,
    {
      resolve: (payload: IRPC['response']['payload']) => void
      reject: (payload: IRPC['response']['payload']) => void
    }
  > = new Map()

  async init() {
    // Import electron dynamically so that it only gets imported when this transport is initialized
    const { ipcRenderer } = await import('electron')
    this.ipcRenderer = ipcRenderer

    ipcRenderer.on('-prisma', this.onIPCMessage.bind(this))
    ipcRenderer.on('-project', this.onIPCMessage.bind(this))
    ipcRenderer.on('-window', this.onIPCMessage.bind(this))
    ipcRenderer.on('-update', this.onIPCMessage.bind(this))
    ipcRenderer.on('-telemetry', this.onIPCMessage.bind(this))
  }

  public request<T extends IRPC>(
    request: Omit<T['request'], 'requestId'>
  ): Promise<T['response']['payload']> {
    const requestId = this.requestIdCounter
    this.requestIdCounter++

    return new Promise((resolve, reject) => {
      this.requests.set(requestId, {
        resolve,
        reject,
      })

      this.ipcRenderer.send(request.channel, {
        requestId,
        action: request.action,
        payload: request.payload,
      })
    })
  }

  // ******************************************************
  // Private methods
  //
  private onIPCMessage(_: any, msg: string) {
    const response = deserialize(msg) as IRPC['response']
    // As long as the request succeeds, the promise should be resolved. It is not the transport's responsibility
    // to handle payload errors
    this.requests.get(response.requestId)?.resolve(response.payload)
    this.requests.delete(response.requestId)
  }
}
